import { Link, usePage, router } from '@inertiajs/react'
import Flash from './Flash'
import Avatar from './Avatar'
import InstallPrompt from './InstallPrompt'
import { clearPrivateCaches } from '../pwa'

function NavLink({ href, active, badge, children }) {
  return (
    <Link
      href={href}
      className={`shrink-0 rounded-full px-3 py-1.5 font-display text-sm font-bold whitespace-nowrap transition sm:px-4 sm:py-2 sm:text-base ${
        active ? 'bg-blush-400 text-white shadow' : 'text-ink-soft hover:bg-white/70 hover:text-ink'
      }`}
    >
      {children}
      {badge > 0 && (
        <span
          title={`${badge} waiting to be let in`}
          className={`ml-1.5 inline-flex min-w-5 justify-center rounded-full px-1.5 text-xs leading-5 ${
            active ? 'bg-white text-blush-500' : 'bg-blush-400 text-white'
          }`}
        >
          {badge}
        </span>
      )}
    </Link>
  )
}

export default function Layout({ children }) {
  const { currentUser, baby, pendingRequestCount } = usePage().props
  const path = usePage().url

  function signOut(event) {
    event.preventDefault()
    clearPrivateCaches()
    router.delete('/session')
  }

  // Friends and family only ever have one destination, so the nav row is for admins.
  const showNav = Boolean(currentUser?.admin)

  return (
    <div className="min-h-screen">
      <Flash />

      <header className="sticky top-0 z-30 border-b-2 border-white/60 bg-white/80 pt-[env(safe-area-inset-top)] backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-3 py-2 sm:gap-3 sm:px-6 sm:py-3">
          <Link href="/" className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none sm:gap-3">
            <span className="animate-float-slow shrink-0 text-2xl sm:text-3xl" aria-hidden="true">
              🍼
            </span>
            <span className="min-w-0">
              <span className="block truncate font-display text-base leading-tight font-extrabold text-ink sm:text-xl">
                {baby?.name || 'Oliver'}
              </span>
              {baby?.age && (
                <span className="block truncate text-[10px] font-bold tracking-wide text-blush-500 uppercase sm:text-xs">
                  {baby.age}
                </span>
              )}
            </span>
          </Link>

          <nav className="ml-auto hidden items-center gap-1 sm:flex">
            <NavLink href="/" active={path === '/'}>
              Album
            </NavLink>
            {showNav && (
              <>
                <NavLink href="/log" active={path.startsWith('/log')}>
                  Feeds &amp; diapers
                </NavLink>
                <NavLink href="/members" active={path.startsWith('/members')} badge={pendingRequestCount}>
                  Friends &amp; family
                </NavLink>
                <NavLink href="/baby/edit" active={path.startsWith('/baby')}>
                  Nursery
                </NavLink>
              </>
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-1">
            <Link
              href="/account/edit"
              className="flex items-center gap-2 rounded-full border-2 border-white bg-white/80 p-1 transition hover:bg-white sm:py-1 sm:pr-3 sm:pl-1"
              title="Your account"
            >
              <Avatar initials={currentUser?.initials} size="sm" />
              <span className="hidden text-sm font-bold text-ink-soft sm:inline">{currentUser?.name}</span>
            </Link>

            <button
              type="button"
              onClick={signOut}
              title="Sign out"
              className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-ink-soft transition hover:bg-white/70 hover:text-blush-500 sm:h-auto sm:w-auto sm:px-3 sm:py-2"
            >
              <span aria-hidden="true" className="text-lg sm:hidden">
                ⏻
              </span>
              <span className="sr-only sm:not-sr-only sm:text-sm sm:font-bold">Sign out</span>
            </button>
          </div>
        </div>

        {showNav && (
          <nav className="flex gap-1 overflow-x-auto px-3 pb-2 sm:hidden">
            <NavLink href="/" active={path === '/'}>
              Album
            </NavLink>
            <NavLink href="/log" active={path.startsWith('/log')}>
              Feeds &amp; diapers
            </NavLink>
            <NavLink href="/members" active={path.startsWith('/members')} badge={pendingRequestCount}>
              Friends &amp; family
            </NavLink>
            <NavLink href="/baby/edit" active={path.startsWith('/baby')}>
              Nursery
            </NavLink>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 pt-5 pb-24 sm:px-6 sm:pt-8 sm:pb-8">
        <InstallPrompt />
        {children}
      </main>

      <footer className="pb-[calc(2.5rem+env(safe-area-inset-bottom))] text-center text-sm text-ink-soft/70">
        Made with 💕 for friends and family
      </footer>
    </div>
  )
}
